import { Controller } from "@hotwired/stimulus";

export default class extends Controller<HTMLElement> {
    static targets = ["form", "input", "utc", "error", "calendar", "label", "countdown", "date"];
    declare readonly errorTarget: HTMLElement;
    declare readonly hasFormTarget: boolean;
    static values = { deadline: String };
    declare readonly formTarget: HTMLFormElement;
    declare readonly inputTarget: HTMLInputElement;
    declare readonly utcTarget: HTMLInputElement;
    declare readonly deadlineValue: string;
    declare readonly calendarTarget: HTMLElement & { value: string; focusedDate: string };
    declare readonly labelTarget: HTMLButtonElement;
    declare readonly hasCountdownTarget: boolean;
    declare readonly countdownTarget: HTMLElement;
    declare readonly dateTarget: HTMLTimeElement;
    private resizeObserver?: ResizeObserver;
    private countdownTimer?: ReturnType<typeof setInterval>;

    connect() { this.updateCountdown(); }

    countdownTargetConnected() {
        this.updateCountdown();
        if (!this.countdownTimer) this.countdownTimer = setInterval(() => this.updateCountdown(), 60_000);
    }

    countdownTargetDisconnected() {
        if (this.countdownTimer) clearInterval(this.countdownTimer);
        this.countdownTimer = undefined;
    }

    deadlineValueChanged() { this.updateCountdown(); }

    private updateCountdown() {
        if (!this.hasCountdownTarget || !this.deadlineValue) return;
        const deadline = new Date(this.deadlineValue);
        if (!Number.isFinite(deadline.getTime())) return;
        const now = new Date();
        const locale = document.documentElement.lang === "fr" ? "fr-FR" : "en-US";
        // Compare local calendar days, including across daylight-saving changes.
        const day = (date: Date) => Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
        const days = Math.round((day(deadline) - day(now)) / 86400_000);
        this.countdownTarget.textContent = deadline <= now
            ? (locale === "fr-FR" ? "À reprendre" : "Ready now")
            : days === 0 ? (locale === "fr-FR" ? "Aujourd’hui" : "Today")
            : new Intl.RelativeTimeFormat(locale, { numeric: "always" }).format(days, "day");
        this.dateTarget.textContent = new Intl.DateTimeFormat(locale, { day: "numeric", month: "short", ...(deadline.getFullYear() !== now.getFullYear() ? { year: "numeric" as const } : {}) }).format(deadline)
            + " · " + new Intl.DateTimeFormat(locale, { hour: "2-digit", minute: "2-digit" }).format(deadline);
        this.dateTarget.title = new Intl.DateTimeFormat(locale, { dateStyle: "full", timeStyle: "short" }).format(deadline);
    }

    async edit() {
        const moduleUrl = "/static/calendar-elements.js";
        await import(moduleUrl);
        if (!this.element.isConnected) return;
        if (this.formTarget.matches(":popover-open")) { this.formTarget.hidePopover(); return; }
        const date = this.deadlineValue ? new Date(this.deadlineValue) : new Date();
        if (!this.deadlineValue) {
            date.setDate(date.getDate() + 1);
            date.setHours(9, 0, 0, 0);
        }
        const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
        this.inputTarget.value = local.toISOString().slice(0, 16);
        this.inputTarget.setCustomValidity("");
        this.calendarTarget.setAttribute("first-day-of-week", "1");
        this.calendarTarget.setAttribute("locale", this.calendarTarget.dataset.locale ?? "fr-FR");
        this.calendarTarget.value = this.deadlineValue ? this.inputTarget.value.slice(0, 10) : "";
        this.calendarTarget.focusedDate = this.inputTarget.value.slice(0, 10);
        const now = new Date();
        const today = new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
        this.calendarTarget.setAttribute("min", today);
        this.formTarget.setAttribute("popover", "auto");
        this.formTarget.hidden = false;
        this.formTarget.showPopover();
        this.position();
        await new Promise(requestAnimationFrame);
        if (!this.element.isConnected || !this.formTarget.matches(":popover-open")) return;
        this.position();
        this.calendarTarget.querySelector<HTMLElement>("calendar-month")?.focus({ preventScroll: true });
    }

    toggle() {
        const open = this.formTarget.matches(":popover-open");
        this.labelTarget.setAttribute("aria-expanded", String(open));
        this.formTarget.hidden = !open;
        if (open) {
            this.element.setAttribute("data-stream-preserve-self", "true");
            this.resizeObserver = new ResizeObserver(this.position);
            this.resizeObserver.observe(this.formTarget);
            window.addEventListener("resize", this.position);
            document.addEventListener("scroll", this.position, true);
        } else {
            this.element.removeAttribute("data-stream-preserve-self");
            this.stopPositioning();
        }
    }

    disconnect() {
        if (this.countdownTimer) clearInterval(this.countdownTimer);
        this.stopPositioning();
    }

    private stopPositioning() {
        this.resizeObserver?.disconnect();
        this.resizeObserver = undefined;
        window.removeEventListener("resize", this.position);
        document.removeEventListener("scroll", this.position, true);
    }

    private position = () => {
        if (!this.formTarget.matches(":popover-open")) return;
        const anchor = this.labelTarget.getBoundingClientRect();
        const panel = this.formTarget.getBoundingClientRect();
        const left = Math.max(8, Math.min(this.deadlineValue ? anchor.left : anchor.right - panel.width, window.innerWidth - panel.width - 8));
        const preferredTop = anchor.bottom + 8 + panel.height <= window.innerHeight - 8
            ? anchor.bottom + 8 : Math.max(8, anchor.top - panel.height - 8);
        const top = Math.max(8, Math.min(preferredTop, window.innerHeight - panel.height - 8));
        Object.assign(this.formTarget.style, { left: `${left}px`, top: `${top}px` });
    };

    selectDate(event: Event) {
        event.stopPropagation();
        this.inputTarget.value = `${this.calendarTarget.value}T${this.inputTarget.value.split("T")[1]}`;
        this.save();
    }

    save() {
        this.inputTarget.setCustomValidity("");
        this.formTarget.requestSubmit();
    }

    prepare(event: CustomEvent<{ parameters: Record<string, string> }>) {
        const date = new Date(this.inputTarget.value);
        if (!Number.isFinite(date.getTime()) || date.getTime() <= Date.now() ||
            new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16) !== this.inputTarget.value) {
            event.preventDefault();
            event.stopImmediatePropagation();
            this.inputTarget.setCustomValidity("Choisissez une date future valide.");
            this.inputTarget.reportValidity();
            this.inputTarget.oninput = () => this.inputTarget.setCustomValidity("");
            return;
        }
        this.utcTarget.value = date.toISOString();
        event.detail.parameters.deadline = this.utcTarget.value;
    }

    result(event: CustomEvent<{ successful: boolean; xhr: XMLHttpRequest }>) {
        this.errorTarget.hidden = event.detail.successful;
        this.errorTarget.textContent = event.detail.successful ? "" :
            (event.detail.xhr.status === 409 ? event.detail.xhr.responseText : "La modification n’a pas pu être enregistrée. Réessayez.");
        if (event.detail.successful && this.hasFormTarget && this.formTarget.matches(":popover-open")) this.formTarget.hidePopover();
    }
}
